// GUIDED-FLOW: one field of a flow step, rendered by its type (the engine's FieldDef), and the
// embedded "linked" editors, which write through the routes the rest of the app already uses
// (responsibilities, the seat's KPI set, budgets, the DE's "How I work") and then refetch the run.
import {
  type AnswerValue,
  type FieldDef,
  isRequired,
  type LinkedEditor,
} from "@companyos/domain-work/flows";
import {
  Badge,
  Button,
  Field,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, LockIcon, PaperclipIcon, PlusIcon, UploadIcon, XIcon } from "lucide-react";
import { useRef, useState } from "react";
import type { Api } from "../../api/client";
import { BudgetCreateDialog } from "../agents/budgets-screen";
import { uploadOne } from "../chat/chat-files";
import type { CompanyAccess } from "../company";
import { AddKpiDialog } from "../kpi/seat-kpis";
import { responsibilitiesQuery, seatMetricSetQuery } from "../queries";
import { problemText } from "../structure/parts";
import { ResponsibilityDialog } from "../structure/responsibilities";
import { AgentPolicyEditor, HomeChannelEditor, ModelsEditor } from "./flow-de-editors";
import { type FlowRunView, flowKeys } from "./flow-queries";

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

type FieldProps = {
  field: FieldDef;
  /** Required under the answers (`isRequired`: the top of the chart reports to nobody). */
  required: boolean;
  /** The value the form shows (draft, else saved, else a template's suggestion). */
  value: AnswerValue | undefined;
  /** The saved answer (a sealed field's is `true` once filled). */
  saved: AnswerValue | undefined;
  editable: boolean;
  /** A field of the sealed step: write-only. */
  sealed: boolean;
  error?: string | undefined;
  options: FlowRunView["options"];
  onChange: (value: AnswerValue) => void;
};

const textOf = (v: AnswerValue | undefined) =>
  typeof v === "string" ? v : typeof v === "number" ? String(v) : "";

/** A typed field (everything but `linked` and `confirm`, which have their own cards). */
export function FlowField({
  field: f,
  required,
  value,
  saved,
  editable,
  sealed,
  error,
  options,
  onChange,
}: FieldProps) {
  const id = `flow-field-${f.key}`;
  const filled = sealed && saved === true;
  // A sealed value never comes back: the input starts empty and "Filled" says it is there.
  const shown = sealed && value === true ? undefined : value;
  const label = (
    <span className="flex items-center gap-1.5">
      <FieldLabel htmlFor={id}>
        {f.label}
        {required ? <span className="text-faint"> *</span> : null}
      </FieldLabel>
      <InfoTip label={`About ${f.label.toLowerCase()}`}>{f.info}</InfoTip>
      {filled ? (
        <Badge variant="success" data-slot="flow-filled">
          <LockIcon aria-hidden="true" />
          Filled
        </Badge>
      ) : null}
    </span>
  );
  const control = (() => {
    switch (f.type) {
      case "longtext":
        return (
          <Textarea
            id={id}
            value={textOf(shown)}
            disabled={!editable}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            rows={3}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "number":
        return (
          <Input
            id={id}
            type="number"
            value={textOf(shown)}
            disabled={!editable}
            min={f.min}
            max={f.max}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          />
        );
      case "money":
        return (
          <Input
            id={id}
            type={sealed ? "password" : "text"}
            inputMode="decimal"
            autoComplete="off"
            value={textOf(shown)}
            disabled={!editable}
            placeholder={filled ? "Filled" : (f.placeholder ?? "0.00")}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "secret":
        return (
          <Input
            id={id}
            type="password"
            autoComplete="off"
            value={textOf(shown)}
            disabled={!editable}
            maxLength={f.maxLength}
            placeholder={filled ? "Filled" : f.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "time":
        return (
          <Input
            id={id}
            type="time"
            value={textOf(shown)}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "boolean":
        return (
          <NativeSelect
            id={id}
            value={shown === true ? "yes" : shown === false ? "no" : ""}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "yes")}
          >
            <NativeSelectOption value="">—</NativeSelectOption>
            <NativeSelectOption value="yes">Yes</NativeSelectOption>
            <NativeSelectOption value="no">No</NativeSelectOption>
          </NativeSelect>
        );
      case "select":
      case "seat":
      case "role": {
        const list =
          f.type === "select"
            ? (f.options ?? []).map((o) => ({ id: o.value, label: o.label }))
            : f.type === "seat"
              ? options.seats
              : options.roles;
        return (
          <NativeSelect
            id={id}
            value={textOf(shown)}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          >
            <NativeSelectOption value="">{filled ? "Filled" : "—"}</NativeSelectOption>
            {list.map((o) => (
              <NativeSelectOption key={o.id} value={o.id}>
                {o.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      }
      case "list":
      case "links":
        // One item per line (blank lines are dropped on save).
        return (
          <Textarea
            id={id}
            value={Array.isArray(shown) ? shown.join("\n") : textOf(shown)}
            disabled={!editable}
            placeholder={
              f.placeholder ?? (f.type === "links" ? "One link per line" : "One per line")
            }
            rows={4}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value.split("\n"))}
          />
        );
      case "multiselect": {
        const picked = new Set(Array.isArray(shown) ? shown : []);
        return (
          <fieldset id={id} aria-label={f.label} className="m-0 flex flex-wrap gap-2 border-0 p-0">
            {(f.options ?? []).map((o) => (
              <label
                key={o.value}
                className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-sm"
              >
                <input
                  type="checkbox"
                  checked={picked.has(o.value)}
                  disabled={!editable}
                  onChange={(e) => {
                    const next = new Set(picked);
                    if (e.target.checked) next.add(o.value);
                    else next.delete(o.value);
                    onChange((f.options ?? []).map((x) => x.value).filter((v) => next.has(v)));
                  }}
                />
                {o.label}
              </label>
            ))}
          </fieldset>
        );
      }
      default:
        return (
          <Input
            id={id}
            value={textOf(shown)}
            disabled={!editable}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  })();
  return (
    <Field data-slot="flow-field" data-field={f.key} data-invalid={error ? true : undefined}>
      {label}
      {control}
      {error ? <FieldError>{error}</FieldError> : null}
    </Field>
  );
}

/**
 * COLLECT-FLOWS: a files answer. Each picked file is uploaded to the file store at once (the chat
 * staging upload); the answer holds only the ids, and Save binds them to the role-knowledge doc.
 */
export function FilesField({
  api,
  co,
  field: f,
  required,
  value,
  known,
  editable,
  error,
  onChange,
}: {
  api: Api;
  co: string;
  field: FieldDef;
  required: boolean;
  value: AnswerValue | undefined;
  /** Names of files the run already names (the view's `files`). */
  known: readonly { id: string; file_name: string }[];
  editable: boolean;
  error?: string | undefined;
  onChange: (value: AnswerValue) => void;
}) {
  const id = `flow-field-${f.key}`;
  const ids = Array.isArray(value) ? value : [];
  const [names, setNames] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);
  const nameOf = (x: string) => names[x] ?? known.find((k) => k.id === x)?.file_name ?? "File";
  // The answer as it stands now: an upload finishing later adds to it, never to an old copy.
  const current = useRef(ids);
  current.current = ids;
  async function pick(list: FileList | null) {
    if (!list?.length) return;
    setFailed(null);
    for (const file of Array.from(list)) {
      setBusy((n) => n + 1);
      try {
        const up = await uploadOne(api, co, file, () => {});
        setNames((m) => ({ ...m, [up.id]: up.file_name }));
        current.current = [...current.current, up.id];
        onChange(current.current);
      } catch (e) {
        setFailed(e instanceof Error ? e.message : "Upload failed");
      } finally {
        setBusy((n) => n - 1);
      }
    }
  }
  return (
    <Field data-slot="flow-field" data-field={f.key} data-invalid={error ? true : undefined}>
      <span className="flex items-center gap-1.5">
        <FieldLabel htmlFor={id}>
          {f.label}
          {required ? <span className="text-faint"> *</span> : null}
        </FieldLabel>
        <InfoTip label={`About ${f.label.toLowerCase()}`}>{f.info}</InfoTip>
      </span>
      {ids.length ? (
        <ul className="flex flex-col gap-1" data-slot="flow-files">
          {ids.map((x) => (
            <li key={x} className="group/file flex min-w-0 items-center gap-1.5 text-sm">
              <PaperclipIcon className="size-3.5 shrink-0 text-faint" aria-hidden="true" />
              <span className="min-w-0 truncate text-secondary">{nameOf(x)}</span>
              {editable ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${nameOf(x)}`}
                  className="opacity-0 group-hover/file:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                  onClick={() => {
                    const rest = current.current.filter((y) => y !== x);
                    current.current = rest;
                    onChange(rest.length ? rest : null);
                    // Not saved yet: still staged, so it is discarded (frees the staging slot).
                    if (!known.some((k) => k.id === x))
                      void api.client.attachments
                        .discard({ params: { co, id: x } })
                        .catch(() => {});
                  }}
                >
                  <XIcon aria-hidden="true" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {editable ? (
        <label className="self-start">
          <input
            id={id}
            type="file"
            multiple
            className="sr-only"
            onChange={(e) => {
              void pick(e.target.files);
              e.target.value = "";
            }}
          />
          <span className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">
            <UploadIcon className="size-3.5" aria-hidden="true" />
            {busy ? "Uploading…" : "Upload"}
          </span>
        </label>
      ) : !ids.length ? (
        <p className="text-faint text-sm">None yet</p>
      ) : null}
      {failed ? <FieldError>{failed}</FieldError> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </Field>
  );
}

/** DE onboarding's lanes: where it escalates and whom it hands off to, for a person to confirm. */
export function LanesCard({
  field: f,
  view,
  confirmed,
  editable,
  pending,
  onConfirm,
}: {
  field: FieldDef;
  view: FlowRunView;
  confirmed: boolean;
  editable: boolean;
  pending: boolean;
  onConfirm: () => void;
}) {
  const lanes = view.lanes;
  return (
    <div
      data-slot="flow-lanes"
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 text-sm"
    >
      <span className="flex items-center gap-1.5 font-medium">
        {f.label}
        <InfoTip label={`About ${f.label.toLowerCase()}`}>{f.info}</InfoTip>
      </span>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">Escalates to</dt>
        <dd>
          {lanes?.escalate_to
            ? `${lanes.escalate_to.title}${lanes.escalate_to.holders.length ? ` · ${lanes.escalate_to.holders.join(", ")}` : ""}`
            : "No supervisor seat yet"}
        </dd>
        <dt className="text-muted-foreground">Hands off to</dt>
        <dd>{lanes?.peers.length ? lanes.peers.map((p) => p.title).join(", ") : "None"}</dd>
      </dl>
      {confirmed ? (
        <Badge variant="success" className="self-start">
          <CheckIcon aria-hidden="true" />
          Confirmed
        </Badge>
      ) : editable ? (
        <Button size="sm" className="self-start" disabled={pending} onClick={onConfirm}>
          Confirm
        </Button>
      ) : null}
    </div>
  );
}

/** An embedded editor: the rows behind a `linked` field, written through their own routes. */
export function LinkedField({
  api,
  company,
  view,
  field: f,
  editable,
}: {
  api: Api;
  company: CompanyAccess;
  view: FlowRunView;
  field: FieldDef;
  editable: boolean;
}) {
  const editor = (f.bind.kind === "linked" ? f.bind.editor : "responsibilities") as LinkedEditor;
  const count = typeof view.answers[f.key] === "number" ? (view.answers[f.key] as number) : 0;
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: flowKeys.all(company.id) });
  const head = (
    <span className="flex items-center gap-1.5 font-medium">
      {f.label}
      {isRequired(f, view.answers) ? <span className="text-faint"> *</span> : null}
      <InfoTip label={`About ${f.label.toLowerCase()}`}>{f.info}</InfoTip>
    </span>
  );
  return (
    <div
      data-slot="flow-linked"
      data-editor={editor}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 text-sm"
    >
      {head}
      {!editable ? (
        <p className="text-muted-foreground" data-slot="linked-count">
          {count ? "In place" : "Not yet"}
        </p>
      ) : editor === "responsibilities" ? (
        <Responsibilities api={api} company={company} view={view} onDone={refresh} />
      ) : editor === "kpis" ? (
        <Kpis api={api} company={company} view={view} onDone={refresh} />
      ) : editor === "budget" ? (
        <Budget api={api} company={company} view={view} count={count} onDone={refresh} />
      ) : editor === "agent_policy" ? (
        <AgentPolicyEditor api={api} company={company} view={view} count={count} onDone={refresh} />
      ) : editor === "models" ? (
        <ModelsEditor api={api} company={company} view={view} count={count} onDone={refresh} />
      ) : editor === "home_channel" ? (
        <HomeChannelEditor api={api} company={company} view={view} count={count} onDone={refresh} />
      ) : (
        <HowIWorkEditor api={api} company={company} view={view} onDone={refresh} />
      )}
    </div>
  );
}

type EditorProps = { api: Api; company: CompanyAccess; view: FlowRunView; onDone: () => void };

function Responsibilities({ api, company, view, onDone }: EditorProps) {
  const [adding, setAdding] = useState(false);
  const q = useInfiniteQuery(responsibilitiesQuery(api, company.id, view.seat_id));
  const list = (q.data?.pages ?? []).flatMap((p) => p.data).filter((r) => r.status !== "archived");
  return (
    <>
      {list.length ? (
        <ul className="flex flex-col gap-1" data-slot="linked-rows">
          {list.slice(0, 8).map((r) => (
            <li key={r.id} className="truncate text-secondary">
              {r.name}
            </li>
          ))}
          {list.length > 8 ? <li className="text-faint">+{list.length - 8} more</li> : null}
        </ul>
      ) : (
        <p className="text-faint">None yet</p>
      )}
      <Button variant="outline" size="xs" className="self-start" onClick={() => setAdding(true)}>
        <PlusIcon data-icon="inline-start" aria-hidden="true" />
        Add responsibility
      </Button>
      {adding ? (
        <ResponsibilityDialog
          api={api}
          co={company.id}
          seatId={view.seat_id}
          seatTitle={view.seat_title}
          onClose={() => {
            setAdding(false);
            onDone();
          }}
        />
      ) : null}
    </>
  );
}

function Kpis({ api, company, view, onDone }: EditorProps) {
  const [adding, setAdding] = useState(false);
  const q = useQuery(seatMetricSetQuery(api, company.id, view.seat_id));
  const list = q.data?.data ?? [];
  return (
    <>
      {list.length ? (
        <ul className="flex flex-col gap-1" data-slot="linked-rows">
          {list.map((m) => (
            <li key={m.metric_def_id} className="truncate text-secondary">
              {m.metric_name}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-faint">None yet</p>
      )}
      <Button variant="outline" size="xs" className="self-start" onClick={() => setAdding(true)}>
        <PlusIcon data-icon="inline-start" aria-hidden="true" />
        Add KPI
      </Button>
      {adding ? (
        <AddKpiDialog
          api={api}
          company={company}
          seatId={view.seat_id}
          seatTitle={view.seat_title}
          holder={{
            text: view.assignee_name ?? view.seat_title,
            agent: view.flow_key === "de_onboarding",
          }}
          onClose={() => {
            setAdding(false);
            onDone();
          }}
        />
      ) : null}
    </>
  );
}

function Budget({ api, company, view, count, onDone }: EditorProps & { count: number }) {
  const [adding, setAdding] = useState(false);
  return (
    <>
      <p className={count ? "text-secondary" : "text-faint"} data-slot="linked-count">
        {count ? plural(count, "budget") : "None yet"}
      </p>
      <Button variant="outline" size="xs" className="self-start" onClick={() => setAdding(true)}>
        <PlusIcon data-icon="inline-start" aria-hidden="true" />
        Add budget
      </Button>
      {adding ? (
        <BudgetCreateDialog
          api={api}
          company={company}
          seat={{ id: view.seat_id, title: view.seat_title }}
          onClose={() => {
            setAdding(false);
            onDone();
          }}
        />
      ) : null}
    </>
  );
}

/** "How I work" of the seat's DE (its active digital-employee holder). */
function HowIWorkEditor({ api, company, view, onDone }: EditorProps) {
  const co = company.id;
  // The run names the seat's DE; its profile gives the current text and version (a save sends
  // `base_version`, so it never overwrites an edit made elsewhere).
  const deId = view.de_membership_id;
  const de = useQuery({
    queryKey: ["company", co, "de", deId ?? "none", "flow-how-i-work"] as const,
    queryFn: () => api.client.digitalEmployees.get({ params: { co, id: deId as string } }),
    enabled: deId !== null,
  });
  const [text, setText] = useState<string | null>(null);
  const current = de.data?.how_i_work?.text ?? "";
  const save = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.putHowIWork({
        params: { co, id: deId as string },
        body: {
          text: (text ?? current).trim() || null,
          base_version: de.data?.how_i_work?.version ?? 0,
        },
      }),
    onSuccess: () => {
      setText(null);
      void de.refetch();
      toast.add({ title: "How I work saved", type: "success" });
      onDone();
    },
  });
  if (deId === null) return <p className="text-faint">No digital employee holds this seat yet.</p>;
  return (
    <>
      <Textarea
        aria-label="How I work"
        rows={5}
        maxLength={20000}
        value={text ?? current}
        disabled={!de.data}
        onChange={(e) => setText(e.target.value)}
      />
      {save.error ? <p className="text-danger-text text-xs">{problemText(save.error)}</p> : null}
      <Button
        variant="outline"
        size="xs"
        className="self-start"
        disabled={text === null || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? "Saving…" : "Save How I work"}
      </Button>
    </>
  );
}
